import { useState, useMemo } from "react";

// ── Criteria definitie — synchroon met scoring.js ──────────────────
// Databron: "solid" = IPMA/INE/haversine  "osm" = Overpass API  "manual" = handmatig
const CRITERIA_DEF = [
  // Gezin
  { id: "youngFamilies",      label: "Jonge gezinnen",    icon: "👶", source: "solid",  w_default: 9 },
  { id: "montessori",         label: "Montessori-school", icon: "🏫", source: "solid",  w_default: 7 },
  { id: "forestSchool",       label: "Bosschool",         icon: "🌳", source: "solid",  w_default: 7 },
  { id: "services",           label: "Voorzieningen",     icon: "🏥", source: "osm"   , w_default: 5 },
  // Klimaat
  { id: "climate",            label: "Klimaat / hitte",   icon: "🌡", source: "solid",  w_default: 8 },
  { id: "dryness",            label: "Neerslag",          icon: "🌧", source: "solid",  w_default: 6 },
  { id: "sunshine",           label: "Zonneschijn",       icon: "☀", source: "solid",  w_default: 7 },
  // Lifestyle
  { id: "surf",               label: "Surf-cultuur",      icon: "🏄", source: "osm",    w_default: 6 },
  { id: "wellness",           label: "Wellness / yoga",   icon: "🧘", source: "osm",    w_default: 6 },
  { id: "coffee",             label: "Community / café",  icon: "☕", source: "osm"   , w_default: 6 },
  // Slow-living
  { id: "natureAccess",       label: "Natuur-toegang",    icon: "🌿", source: "osm",    w_default: 9 },
  { id: "tranquility",        label: "Rust",              icon: "🔇", source: "osm"   , w_default: 8 },
  { id: "culturalAuthenticity", label: "Auth. cultuur",   icon: "🏛", source: "manual", w_default: 7 },
  { id: "foodAutonomy",       label: "Voedselautonomie",  icon: "🌾", source: "osm"   , w_default: 8 },
];

const GROUPS = [
  { id: "family",    icon: "🏡", label: "Voor het gezin",
    ids: ["youngFamilies", "montessori", "forestSchool", "services"] },
  { id: "climate",   icon: "🌡", label: "Klimaat",
    ids: ["climate", "dryness", "sunshine"] },
  { id: "lifestyle", icon: "🌊", label: "Lifestyle",
    ids: ["surf", "wellness", "coffee"] },
  { id: "slow",      icon: "🌿", label: "Slow-living",
    ids: ["natureAccess", "tranquility", "culturalAuthenticity", "foodAutonomy"] },
];

const SOURCE_BADGE = {
  solid:  { label: "✓ Data",      style: { background: "#d4edda", color: "#2d6a4f" } },
  osm:    { label: "🗺 OSM",      style: { background: "#d0e8ff", color: "#1a5a9a" } },
  manual: { label: "✏ Handmatig", style: { background: "#fff3cd", color: "#856404" } },
};

const SCORE_COLORS = ["", "#E24B4A", "#EF9F27", "#BA7517", "#639922", "#3B6D11"];

function WeightDots({ weight, max = 9 }) {
  return (
    <div className="se-dots" aria-label={`Gewicht ${weight} van ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={`se-dot${i < weight ? " on" : ""}`} />
      ))}
    </div>
  );
}

function ScoreBar({ label, score, weight }) {
  const pct = Math.round((score / 5) * 100);
  const contribution = score * weight;
  return (
    <div className="se-bar-row" title={`${score}/5 × gewicht ${weight} = ${contribution} pt`}>
      <span className="se-bar-label">{label}</span>
      <div className="se-bar-track">
        <div
          className="se-bar-fill"
          style={{ width: `${pct}%`, background: SCORE_COLORS[score] || "#888" }}
        />
      </div>
      <span className="se-bar-score">{score}/5</span>
      <span className="se-bar-weight">×{weight}</span>
      <span className="se-bar-pts">{contribution}pt</span>
    </div>
  );
}

export function ScoreExplainer({ towns = [], weights = {} }) {
  const [open, setOpen]               = useState(false);
  const [selectedName, setSelectedName] = useState(null);
  const [activeTab, setActiveTab]     = useState("breakdown"); // "breakdown" | "guide"

  const criteriaMap = useMemo(() =>
    Object.fromEntries(CRITERIA_DEF.map(c => [c.id, c])), []);

  const exampleTown = useMemo(() => {
    if (selectedName) return towns.find(t => t.name === selectedName) || towns[0];
    return towns[0];
  }, [towns, selectedName]);

  const scores = exampleTown?.scores || {};

  const { total, maxTotal } = useMemo(() => {
    let tot = 0, mx = 0;
    for (const c of CRITERIA_DEF) {
      const w = weights[c.id] ?? c.w_default;
      if (!w) continue;
      tot += (scores[c.id] ?? 0) * w;
      mx  += 5 * w;
    }
    return { total: tot, maxTotal: mx };
  }, [scores, weights]);

  const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;

  // Actieve criteria (gewicht > 0), gesorteerd op bijdrage aflopend
  const activeCriteria = useMemo(() =>
    CRITERIA_DEF
      .filter(c => (weights[c.id] ?? c.w_default) > 0)
      .map(c => ({ ...c, w: weights[c.id] ?? c.w_default, s: scores[c.id] ?? 0 }))
      .sort((a, b) => (b.s * b.w) - (a.s * a.w)),
    [scores, weights]);

  return (
    <div className="se-wrap">
      <button
        className="se-toggle-btn"
        onClick={() => setOpen(o => !o)}
        type="button"
        aria-expanded={open}
      >
        <span className="se-toggle-icon">{open ? "▲" : "▼"}</span>
        Hoe werkt de score?
      </button>

      {open && (
        <div className="se-panel">

          {/* ── Formule ── */}
          <div className="se-formula">
            <div className="se-formula-step">
              <strong>Score</strong>
              <span>1–5 per criterium</span>
            </div>
            <div className="se-formula-op">×</div>
            <div className="se-formula-step">
              <strong>Gewicht</strong>
              <span>jouw schuifregelaar</span>
            </div>
            <div className="se-formula-op">÷</div>
            <div className="se-formula-step">
              <strong>Max</strong>
              <span>5 × gewicht</span>
            </div>
            <div className="se-formula-op">=</div>
            <div className="se-formula-step se-formula-result">
              <strong>0–100%</strong>
              <span>ranking</span>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="se-tabs" role="tablist">
            <button role="tab" aria-selected={activeTab === "breakdown"}
              className={`se-tab${activeTab === "breakdown" ? " active" : ""}`}
              onClick={() => setActiveTab("breakdown")} type="button">
              Score-opbouw
            </button>
            <button role="tab" aria-selected={activeTab === "guide"}
              className={`se-tab${activeTab === "guide" ? " active" : ""}`}
              onClick={() => setActiveTab("guide")} type="button">
              Criteria &amp; databronnen
            </button>
          </div>

          {/* ── Tab: score-opbouw ── */}
          {activeTab === "breakdown" && towns.length > 0 && (
            <div className="se-example">
              <div className="se-example-header">
                <select
                  className="se-select"
                  value={exampleTown?.name || ""}
                  onChange={e => setSelectedName(e.target.value)}
                >
                  {[...towns]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(t => (
                      <option key={t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
                <div className="se-total-inline">
                  <span className="se-total-pct">{pct}%</span>
                  <span className="se-total-pts">{total}/{maxTotal}pt</span>
                </div>
              </div>

              <div className="se-bars">
                {activeCriteria.map(c => (
                  <ScoreBar key={c.id} label={`${c.icon} ${c.label}`} score={c.s} weight={c.w} />
                ))}
              </div>
            </div>
          )}

          {/* ── Tab: criteria & databronnen ── */}
          {activeTab === "guide" && (
            <div className="se-groups">
              {GROUPS.map(g => (
                <div key={g.id} className="se-group">
                  <div className="se-group-header">
                    <span>{g.icon}</span> {g.label}
                  </div>
                  {g.ids.map(id => {
                    const c = criteriaMap[id];
                    if (!c) return null;
                    const w = weights[id] ?? c.w_default;
                    const badge = SOURCE_BADGE[c.source];
                    return (
                      <div key={id} className="se-criterion">
                        <span className="se-crit-label">{c.icon} {c.label}</span>
                        <span className="se-source-badge" style={badge.style}>{badge.label}</span>
                        <WeightDots weight={w} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* ── Veto's ── */}
          <div className="se-vetos">
            <strong>Harde veto's</strong> — locaties die hieraan raken worden uitgesloten (🚫):
            <ul>
              <li>Zomerpiek boven 39°C (IPMA 1991–2024)</li>
              <li>Meer dan 30 hittedagen per jaar (&gt;35°C)</li>
              <li>Zee verder dan 45 minuten rijden</li>
              <li>Water-status "extreme" — schakelaar in zijbalk</li>
            </ul>
            <div className="se-veto-note">
              OSM-scores (🗺) worden automatisch bijgewerkt bij een volgende serverscan.
              Handmatige scores (✏) zijn ingevoerd bij opzet en kunnen verouderen.
            </div>
          </div>

        </div>
      )}
    </div>
  );
}